import { type BannerId } from '@databases/banner/BannerContext';
import { useBanners } from '@databases/banner/useBanners';
import { useIsAnyViewSettingsMenuOpen } from '@databases/view-settings/useIsAnyViewSettingsMenuOpen';
import React, { type ComponentType, lazy, Suspense } from 'react';

import { dynamicImport } from '@canvas/dynamic-import';

const LazyUnsavedViewChangesBanner = lazy(() =>
  dynamicImport(() => import('@databases/views/UnsavedViewChangesBanner')).then(
    (module) => ({
      default: module.UnsavedViewChangesBanner,
    })
  )
);

/** Wraps UnsavedViewChangesBanner so that it can be suppressed while a view settings menu is open. */
const SuppressableViewChangesBanner = ({ bannerId }: { bannerId: BannerId }) => {
  const suppressed = useIsAnyViewSettingsMenuOpen();
  return <LazyUnsavedViewChangesBanner bannerId={bannerId} suppressed={suppressed} />;
};

const BannerMap: { [key in BannerId]: ComponentType<{ bannerId: BannerId }> } = {
  'unsaved-view-changes': SuppressableViewChangesBanner,
} as const;

type BannerProps = {
  id: BannerId;
};

const Banner = React.memo<BannerProps>(function Banner({ id }) {
  const BannerComponent = BannerMap[id];

  return (
    <Suspense>
      <BannerComponent bannerId={id} />
    </Suspense>
  );
});

export const Banners = () => {
  const { activeBanners } = useBanners();
  return (
    <>
      {activeBanners.map((id) => {
        return <Banner key={id} id={id} />;
      })}
    </>
  );
};
